<template>
  <div>
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardBody class="p-4">
            <CButton color="link" class="px-0 mb-2" @click="cancel">
              <CIcon name="cil-arrow-left" /> Journal entries
            </CButton>
            <h1 class="h4 mb-1 font-weight-bold">
              {{
                obj.id
                  ? `Journal entry ${obj.journalNo || ""}`.trim()
                  : "Add journal entry"
              }}
            </h1>
            <p class="text-muted mb-4">
              Debits and credits must be equal before you can save
            </p>
            <CForm>
              <CInput
                v-if="obj.id"
                readonly
                label="Journal no."
                horizontal
                v-model="obj.journalNo"
              />
              <CInput
                type="date"
                :value="computeTransactionDate"
                @change="setTransactionDate"
                label="Date *"
                horizontal
              />
              <CInput
                label="Description"
                v-model="obj.description"
                horizontal
              />

              <h2 class="h6 font-weight-bold mt-4 mb-3">Lines</h2>

              <!-- Desktop: table -->
              <div class="d-none d-lg-block">
                <CDataTable :items="obj.items" :fields="journalFields">
                  <template #show_index="{ index }">
                    <td class="py-2">
                      {{ index + 1 }}
                    </td>
                  </template>
                  <template #show_account="{ item }">
                    <td>
                      <v-select
                        class="w-100"
                        v-model="item.chartAccount"
                        :label="`itemDisplay`"
                        :options="computedChartAccountItems"
                        placeholder="Choose an account"
                        :aria-label="`Account for line ${item.position}`"
                      />
                    </td>
                  </template>
                  <template #show_description="{ item }">
                    <td>
                      <CInput
                        v-model="item.description"
                        :aria-label="`Description for line ${item.position}`"
                        class="mb-0"
                      />
                    </td>
                  </template>
                  <template #show_debit="{ item }">
                    <td>
                      <CInput
                        v-model="item.debit"
                        type="number"
                        inputmode="decimal"
                        min="0"
                        step="0.01"
                        prepend="RM"
                        class="mb-0"
                        :aria-label="`Debit for line ${item.position}`"
                      />
                    </td>
                  </template>
                  <template #show_credit="{ item }">
                    <td>
                      <CInput
                        v-model="item.credit"
                        type="number"
                        inputmode="decimal"
                        min="0"
                        step="0.01"
                        prepend="RM"
                        class="mb-0"
                        :aria-label="`Credit for line ${item.position}`"
                      />
                    </td>
                  </template>
                  <template #show_move="{ index }">
                    <td class="py-2 text-nowrap">
                      <CButton
                        color="light"
                        size="sm"
                        class="mr-1"
                        :aria-label="`Move line ${index + 1} up`"
                        :disabled="index === 0"
                        @click="moveItem(index, 'up')"
                      >
                        <CIcon name="cil-arrow-top" />
                      </CButton>
                      <CButton
                        color="light"
                        size="sm"
                        :aria-label="`Move line ${index + 1} down`"
                        :disabled="index === obj.items.length - 1"
                        @click="moveItem(index, 'down')"
                      >
                        <CIcon name="cil-arrow-bottom" />
                      </CButton>
                    </td>
                  </template>
                  <template #show_remove="{ item, index }">
                    <td class="py-2">
                      <CButton
                        color="light"
                        size="sm"
                        :aria-label="`Remove line ${index + 1}`"
                        @click="onRemoveItem(item)"
                      >
                        <CIcon name="cil-trash" class="text-danger" />
                      </CButton>
                    </td>
                  </template>

                  <template #footer>
                    <tr>
                      <td colspan="3">
                        <CButton color="light" @click="addNewItem()">
                          <CIcon name="cil-plus" /> Add line
                        </CButton>
                      </td>
                      <td class="text-right font-weight-bold">
                        RM {{ formatMyr(totalDebit) }}
                      </td>
                      <td class="text-right font-weight-bold">
                        RM {{ formatMyr(totalCredit) }}
                      </td>
                      <td colspan="2"></td>
                    </tr>
                  </template>
                </CDataTable>
              </div>

              <!-- Mobile: cards -->
              <div class="d-lg-none">
                <div
                  v-for="(item, index) in obj.items || []"
                  :key="item.id"
                  class="border rounded p-3 mb-2"
                >
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="font-weight-bold">Line {{ index + 1 }}</span>
                    <div class="text-nowrap">
                      <CButton
                        color="light"
                        class="mr-1"
                        :aria-label="`Move line ${index + 1} up`"
                        :disabled="index === 0"
                        @click="moveItem(index, 'up')"
                      >
                        <CIcon name="cil-arrow-top" />
                      </CButton>
                      <CButton
                        color="light"
                        class="mr-1"
                        :aria-label="`Move line ${index + 1} down`"
                        :disabled="index === obj.items.length - 1"
                        @click="moveItem(index, 'down')"
                      >
                        <CIcon name="cil-arrow-bottom" />
                      </CButton>
                      <CButton
                        color="light"
                        :aria-label="`Remove line ${index + 1}`"
                        @click="onRemoveItem(item)"
                      >
                        <CIcon name="cil-trash" class="text-danger" />
                      </CButton>
                    </div>
                  </div>
                  <CInput label="Description" v-model="item.description" />
                  <div class="form-group">
                    <label>Account *</label>
                    <v-select
                      v-model="item.chartAccount"
                      :label="`itemDisplay`"
                      :options="computedChartAccountItems"
                      placeholder="Choose an account"
                    />
                  </div>
                  <CRow>
                    <CCol xs="6">
                      <CInput
                        label="Debit"
                        v-model="item.debit"
                        type="number"
                        inputmode="decimal"
                        min="0"
                        step="0.01"
                        prepend="RM"
                      />
                    </CCol>
                    <CCol xs="6">
                      <CInput
                        label="Credit"
                        v-model="item.credit"
                        type="number"
                        inputmode="decimal"
                        min="0"
                        step="0.01"
                        prepend="RM"
                      />
                    </CCol>
                  </CRow>
                </div>
                <CButton color="light" class="mb-3" @click="addNewItem()">
                  <CIcon name="cil-plus" /> Add line
                </CButton>
                <div class="d-flex justify-content-between font-weight-bold">
                  <span>Total debit</span>
                  <span>RM {{ formatMyr(totalDebit) }}</span>
                </div>
                <div class="d-flex justify-content-between font-weight-bold mb-3">
                  <span>Total credit</span>
                  <span>RM {{ formatMyr(totalCredit) }}</span>
                </div>
              </div>

              <CAlert v-if="accountError" color="danger" class="mb-2">
                {{ accountError }}
              </CAlert>
              <CAlert v-if="balanceError" color="danger" class="mb-0">
                {{ balanceError }}
              </CAlert>
            </CForm>
          </CCardBody>
          <CCardFooter class="d-flex justify-content-between">
            <CButton color="light" :disabled="saving" @click="cancel">
              Cancel
            </CButton>
            <CButton color="primary" :disabled="saving" @click="submit">
              <CSpinner v-if="saving" size="sm" class="mr-1" />
              {{ obj.id ? "Save" : "Add journal entry" }}
            </CButton>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import JournalEntryApi from "@/lib/journalEntryApi";
import ChartOfAccountApi from "../../lib/chartOfAccountApi";
import { formatMyr } from "@/utils/money";
import { asDateString, mytToday, parseDateInput } from "@/utils/datetime";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "JournalEntry",
  components: {
    vSelect,
  },
  data: () => {
    return {
      journalFields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
        {
          key: "show_description",
          label: "Description",
        },
        {
          key: "show_account",
          label: "Account *",
        },
        {
          key: "show_debit",
          label: "Debit",
          _style: "width:160px",
        },
        {
          key: "show_credit",
          label: "Credit",
          _style: "width:160px",
        },
        {
          key: "show_move",
          label: "",
          _style: "width:1%",
        },
        {
          key: "show_remove",
          label: "",
          _style: "width:1%",
        },
      ],

      loading: false,
      saving: false,
      chartOfAccountItems: [],
      chartOfAccountApi: new ChartOfAccountApi(),
      obj: {
        date: mytToday(),
        items: [],
      },
      submitted: false,
      balanceError: "",
      accountError: "",
      api: new JournalEntryApi(),
    };
  },
  mounted() {
    this.fetchChartOfAccount();
  },
  computed: {
    totalDebit() {
      let total = 0;
      for (const item of this.obj.items || []) {
        const debit = parseFloat(item.debit);
        if (!isNaN(debit)) {
          total += debit;
        }
      }
      return total;
    },
    totalCredit() {
      let total = 0;
      for (const item of this.obj.items || []) {
        const credit = parseFloat(item.credit);
        if (!isNaN(credit)) {
          total += credit;
        }
      }
      return total;
    },

    computedChartAccountItems() {
      return this.chartOfAccountItems.map((item) => {
        return {
          ...item,
          itemDisplay: `${item.accountNo} - ${
            item.category
          } - ${item.name.trim()}`,
        };
      });
    },

    computeTransactionDate() {
      return asDateString(this.obj.date);
    },
  },
  watch: {
    totalDebit() {
      this.balanceError = "";
    },
    totalCredit() {
      this.balanceError = "";
    },
  },
  methods: {
    formatMyr,

    generateGUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          var r = (Math.random() * 16) | 0,
            v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },
    addNewItem() {
      if (!this.obj.items) this.$set(this.obj, "items", []);
      this.obj.items.push({
        id: this.generateGUID(),
        chartAccount: null,
        debit: 0,
        credit: 0,
        description: "",
        position: this.obj.items.length + 1,
      });
    },

    moveItem(index, direction) {
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex >= 0 && newIndex < this.obj.items.length) {
        const temp = this.obj.items[index];
        this.$set(this.obj.items, index, this.obj.items[newIndex]);
        this.$set(this.obj.items, newIndex, temp);

        // Update positions after swapping
        this.obj.items[index].position = index + 1;
        this.obj.items[newIndex].position = newIndex + 1;
      }
    },
    onRemoveItem(item) {
      if (this.obj.items != null) {
        for (var i = 0; i < this.obj.items.length; i++) {
          if (this.obj.items[i].id === item.id) {
            this.obj.items.splice(i, 1);
          }
        }
      }

      this.updatePositions();
    },
    updatePositions() {
      (this.obj.items || []).forEach((it, i) => {
        it.position = i + 1;
      });
    },

    findChartAccount(id) {
      return (
        this.computedChartAccountItems.find((item) => item.id === id) || null
      );
    },
    fetchChartOfAccount() {
      this.chartOfAccountApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.chartOfAccountItems = response.result;
          this.resetObj();
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error(
            "Couldn't load your chart of accounts. Reload the page to try again."
          );
        });
    },
    setTransactionDate(e) {
      this.obj.date = parseDateInput(e);
    },

    cancel() {
      this.$router.push({ path: "/tenants/JournalEntryList" });
    },
    resetObj() {
      if (this.$route.params.id) {
        this.api
          .get(this.$route.params.id)
          .then((response) => {
            this.obj = response.result;
          })
          .catch((err) => {
            console.error(err);
            this.$toast.error(
              "Couldn't load this journal entry. Go back to the list and try again."
            );
          });
      } else {
        this.obj = this.getEmptyObj();
      }
    },
    onSubmit() {
      const items = this.obj.items || [];
      let haveError = false;
      items.forEach((journalitem) => {
        if (journalitem.chartAccount == null) {
          haveError = true;
        } else {
          journalitem.chartAccountId = journalitem.chartAccount.id;
        }
      });

      if (haveError) {
        this.accountError = "Choose an account for every line.";
        return;
      }
      this.accountError = "";

      // Compare in cents to avoid floating-point drift.
      const debitCents = Math.round(this.totalDebit * 100);
      const creditCents = Math.round(this.totalCredit * 100);
      if (debitCents !== creditCents || debitCents === 0) {
        this.balanceError =
          debitCents === 0
            ? "Enter at least one debit and one credit. Debits and credits must be equal and not zero."
            : `Debits (RM ${formatMyr(this.totalDebit)}) and credits (RM ${formatMyr(this.totalCredit)}) must be equal.`;
        return;
      }
      this.balanceError = "";

      this.saving = true;
      if (!this.obj.id) {
        this.api
          .create(this.obj)
          .then((response) => {
            this.$toast.success("Journal entry added");
            const newObj = response.result;
            this.$router.push({
              path: `/tenants/journalEntry/${newObj.id}`,
            });
          })
          .catch(({ data } = {}) => {
            this.$toast.error(
              `Couldn't save the journal entry. ${helper.getErrorMessage(data)}`
            );
          })
          .finally(() => {
            this.saving = false;
          });
      } else {
        this.api
          .update(this.obj)
          .then(() => {
            this.$toast.success("Journal entry saved");
            this.resetObj();
          })
          .catch(({ data } = {}) => {
            this.$toast.error(
              `Couldn't save the journal entry. ${helper.getErrorMessage(data)}`
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    getEmptyObj() {
      return {
        date: mytToday(),
        items: [],
      };
    },
    submit() {
      if (this.saving) return;
      this.onSubmit();
      this.submitted = true;
    },
  },
};
</script>
